<template>
  <v-container fluid class="pa-5 seller-page">
    <v-overlay :value="isLoading" absolute>
      <div class="d-flex flex-column align-center">
        <v-progress-circular indeterminate size="56" width="4" color="#F4811F" />
        <span class="mt-4 white--text font-weight-medium">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <!-- Page Header -->
    <div class="page-header mb-6">
      <div>
        <h1 class="page-title">จัดการสินค้า</h1>
        <p class="page-subtitle">{{ totalItems }} รายการทั้งหมด</p>
      </div>
      <v-btn color="#F4811F" dark class="action-btn" @click="handleAddProduct">
        <v-icon left>mdi-plus</v-icon>เพิ่มสินค้าใหม่
      </v-btn>
    </div>

    <!-- Filter card -->
    <div class="s-card mb-5">
      <div class="s-card-body">
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              placeholder="ค้นหาสินค้า (ชื่อ, SKU)"
              outlined dense
              prepend-inner-icon="mdi-magnify"
              hide-details="auto"
              clearable
              background-color="#f9fafb"
              class="rounded-lg"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              item-text="label"
              item-value="value"
              placeholder="กรองตามสถานะ"
              outlined dense
              hide-details="auto"
              clearable
              background-color="#f9fafb"
              class="rounded-lg"
            />
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Table card -->
    <div class="s-card">
      <div class="s-card-header">
        <div class="s-card-icon" style="background:#fff7ed">
          <v-icon color="#F4811F" size="18">mdi-package-variant-outline</v-icon>
        </div>
        <span class="s-card-title">รายการสินค้า</span>
        <span class="s-card-badge">{{ totalItems }} รายการ</span>
      </div>
      <div class="s-card-body">
        <template v-if="filteredProducts.length > 0">
          <ProductTable
            :products="filteredProducts"
            @edit="handleEdit"
            @delete="handleDelete"
            @toggle-status="handleToggleStatus"
          />
          <div v-if="totalPages > 1" class="d-flex align-center justify-space-between flex-wrap mt-4" style="gap:12px">
            <div class="d-flex align-center" style="gap:8px">
              <span class="text-caption grey--text">แสดงต่อหน้า:</span>
              <v-select
                v-model="itemsPerPage"
                :items="[10, 20, 50, 100]"
                dense outlined hide-details
                style="max-width:90px"
                class="rounded-lg"
              />
            </div>
            <v-pagination
              v-model="page"
              :length="totalPages"
              :total-visible="7"
              color="#F4811F"
            />
          </div>
        </template>
        <div v-else class="empty-state">
          <img src="@/assets/not_exist_search.svg" alt="no-data" width="88" height="88" />
          <p class="empty-text">ไม่พบสินค้าที่ค้นหา</p>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import ProductTable from "@/components/SellerProducts/Table.vue";
import { deleteSellerProductApi, getSellerProductsApi, updateProductStatusApi } from "@/services/api/sellerServices";
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "SellerProducts",
  components: { ProductTable },
  data() {
    return {
      isLoading: false,
      products: [],
      search: "",
      selectedStatus: null,
      statusOptions: [
        { label: "เปิดใช้งาน", value: "active" },
        { label: "ปิดใช้งาน", value: "inactive" },
        { label: "แบบร่าง", value: "draft" },
      ],
      page: 1,
      itemsPerPage: 10,
      totalItems: 0,
      searchTimeout: null,
    };
  },
  computed: {
    filteredProducts() { return this.products; },
    totalPages() { return Math.ceil(this.totalItems / this.itemsPerPage); },
  },
  created() { this.loadProducts(); },
  watch: {
    search() { this.debounceSearch(); },
    selectedStatus() { this.page = 1; this.loadProducts(); },
    page() { this.loadProducts(); },
    itemsPerPage() { this.page = 1; this.loadProducts(); },
  },
  methods: {
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => { this.page = 1; this.loadProducts(); }, 500);
    },
    async loadProducts() {
      this.isLoading = true;
      try {
        const res = await getSellerProductsApi({ search: this.search || "", status: this.selectedStatus || null, limit: this.itemsPerPage, offset: (this.page - 1) * this.itemsPerPage });
        const raw = res?.data || res?.products || [];
        this.products = raw.map((p) => ({ ...p, name: p.productName || p.name, sku: p.modelCode || p.sku, status: p.status || "active" }));
        this.totalItems = res?.total || res?.count || 0;
      } catch (err) {
        swalAlert.Error("โหลดข้อมูลสินค้าไม่สำเร็จ", err?.response?.data?.message || "ไม่สามารถโหลดข้อมูลสินค้าได้");
        this.products = []; this.totalItems = 0;
      } finally { this.isLoading = false; }
    },
    handleAddProduct() { this.$router.push({ name: "seller_product_add" }); },
    handleEdit(product) { this.$router.push({ name: "seller_product_edit", params: { id: product.id } }); },
    async handleDelete(product) {
      const res = await swalAlert.Dialog("ยืนยันการลบสินค้า", `คุณต้องการลบสินค้า "${product.name}" ใช่หรือไม่?`, require("@/assets/alert/save.png"), 132, 121, "ยืนยัน", "ยกเลิก", true);
      if (!res.isConfirmed) return;
      try {
        await deleteSellerProductApi(product.id);
        this.products = this.products.filter((p) => p.id !== product.id);
        await swalAlert.Success("ลบสินค้าสำเร็จ");
      } catch (err) { swalAlert.Error("ลบสินค้าไม่สำเร็จ", err?.response?.data?.message || "ไม่สามารถลบสินค้าได้"); }
    },
    async handleToggleStatus(product) {
      const isActive = ["active", "published"].includes((product.status || "").toLowerCase());
      const res = await swalAlert.Dialog(isActive ? "ยืนยันการปิดการแสดงสินค้า" : "ยืนยันการเปิดการแสดงสินค้า", `คุณต้องการ${isActive ? "ปิด" : "เปิด"}การแสดงสินค้า "${product.name}" ใช่หรือไม่?`, require("@/assets/alert/save.png"), 132, 121, "ยืนยัน", "ยกเลิก", true);
      if (!res.isConfirmed) return;
      try {
        const newStatus = isActive ? "inactive" : "active";
        await updateProductStatusApi(product.id, newStatus);
        this.products = this.products.map((p) => p.id === product.id ? { ...p, status: newStatus } : p);
        await swalAlert.Success(`${isActive ? "ปิด" : "เปิด"}การแสดงสินค้าสำเร็จ`);
      } catch (err) { swalAlert.Error("อัปเดตสถานะไม่สำเร็จ", err?.response?.data?.message || "ไม่สามารถอัปเดตสถานะได้"); }
    },
  },
};
</script>

<style scoped>
@media (max-width: 599px) {
  .page-header .action-btn { width: 100%; }
}
</style>
